import type React from 'react'
import { useEffect } from 'react'

export interface DeletePlanDialogProps {
  readonly planTitle: string
  readonly onConfirm: () => void
  readonly onCancel: () => void
  readonly busy?: boolean
  readonly error?: string | null
}

export function DeletePlanDialog({
  planTitle,
  onConfirm,
  onCancel,
  busy = false,
  error = null,
}: DeletePlanDialogProps): React.ReactElement {
  useEffect(() => {
    const handleKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape' && !busy) onCancel()
    }
    document.addEventListener('keydown', handleKey)
    return () => document.removeEventListener('keydown', handleKey)
  }, [onCancel, busy])

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="delete-plan-title"
      data-testid="delete-plan-dialog"
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onCancel()
      }}
    >
      <div className="w-full max-w-md rounded-base border border-border bg-surface-elevated p-6 shadow-lg">
        <h2
          id="delete-plan-title"
          className="font-serif text-2xl text-foreground"
        >
          Delete this plan?
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">
          <span className="font-medium text-foreground">{planTitle}</span> and any of its
          configuration will be removed. This cannot be undone.
        </p>
        {error && (
          <div
            className="mt-4 rounded-sm border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
            role="alert"
            data-testid="delete-error"
          >
            {error}
          </div>
        )}
        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            data-testid="delete-cancel"
            className="rounded-full border border-border bg-surface-elevated px-4 py-1.5 text-sm font-medium text-foreground hover:bg-muted disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            data-testid="delete-confirm"
            className="rounded-full bg-destructive px-4 py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
          >
            {busy ? 'Deleting...' : 'Delete plan'}
          </button>
        </div>
      </div>
    </div>
  )
}
